/**
 * Reading a recovery grant out of the URL — and getting it out of the URL again.
 *
 * ── the trap this file exists to handle ─────────────────────────────────────
 *
 * A reset link is a bearer credential that arrives in a URL, and a URL is the
 * single leakiest place to put one:
 *
 *   · it lands in browser history, where the next person at that machine finds
 *     it;
 *   · it is sent in the `Referer` header of every subsequent request the page
 *     makes to another origin;
 *   · it is written verbatim into the access log of every server that sees the
 *     path, and into any front-door analytics;
 *   · corporate mail scanners and link-preview bots FETCH it before the human
 *     does, which for a single-use token means the human's link is already
 *     spent when they click it.
 *
 * Four things address that here, and each is deliberate:
 *
 * 1. **The challenge arrives in the FRAGMENT, never the query.** New mail uses
 *    GoTrue's opaque `token_hash`; older mail may still redirect with an
 *    `access_token`. A fragment is not sent to any server — not to ours, not
 *    in `Referer` — so neither value reaches an access log.
 *
 * 2. **It is removed from the address bar before anything else happens.**
 *    `history.replaceState` — replace, not push, so Back does not walk into the
 *    entry we just removed. Nothing can un-write it from history if we leave it
 *    there for even one render, so this runs at read time, in the same function
 *    that reads it, and not in an effect somewhere.
 *
 * 3. **It is never persisted.** The value is returned and held in React state
 *    for the life of the screen. It is deliberately NOT put through
 *    `AuthClient.set()`, which would write it to localStorage and make a reset
 *    link that was merely opened into a durable session on that device.
 *
 * 4. **A GET cannot spend new recovery mail.** The token hash is exchanged only
 *    after the person presses Continue on the app's recovery screen. A mail
 *    scanner may fetch the page, but it does not perform that human action.
 *    The older access-token redirect remains readable so already-issued links
 *    do not break during rollout.
 */

/** What GoTrue handed back, held in memory only. */
export interface RecoveryGrant {
  /** A real access token for this user, scoped by GoTrue to the recovery flow. */
  accessToken: string
  /** Present on GoTrue's redirect; kept only so it can be revoked on completion. */
  refreshToken: string | null
  /** The email GoTrue put in the token, when it did — labels the screen. */
  email: string | null
}

/** Opaque GoTrue challenge from scanner-safe recovery mail. */
export interface RecoveryChallenge {
  tokenHash: string
}

/** GoTrue reports its own failures in the fragment too (expired, already used). */
export interface RecoveryError {
  code: string
  description: string
}

export interface RecoveryReadResult {
  grant: RecoveryGrant | null
  challenge: RecoveryChallenge | null
  error: RecoveryError | null
}

function claimEmail(jwt: string): string | null {
  try {
    const payload = jwt.split('.')[1]
    if (!payload) return null
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    const claims = JSON.parse(json) as { email?: string }
    return claims.email ?? null
  } catch {
    // Reading the payload is not verification and never was — the server
    // verifies. A malformed token simply means the screen shows no address.
    return null
  }
}

/**
 * Read the fragment, strip it, and say what was there.
 *
 * Call this ONCE, as early as the app can. Calling it twice is safe and returns
 * nothing the second time, which is the correct behaviour rather than a bug: the
 * grant is gone from the URL because this function removed it.
 *
 * @param loc  injectable for tests; defaults to the live location
 * @param hist injectable for tests; defaults to the live history
 */
export function readRecoveryFromHash(
  loc: Pick<Location, 'hash' | 'pathname' | 'search'> | undefined =
    typeof window !== 'undefined' ? window.location : undefined,
  hist: Pick<History, 'replaceState'> | undefined =
    typeof window !== 'undefined' ? window.history : undefined,
): RecoveryReadResult {
  const empty: RecoveryReadResult = { grant: null, challenge: null, error: null }
  if (!loc?.hash || loc.hash.length < 2) return empty

  const params = new URLSearchParams(loc.hash.slice(1))
  const type = params.get('type')
  const token = params.get('access_token')
  const tokenHash = params.get('token_hash')
  const errorCode = params.get('error_code') ?? params.get('error')

  // Only `type=recovery`. An `#access_token` from an ordinary magic-link or
  // OAuth callback is a SIGN-IN, and treating one as a recovery grant would put
  // a "choose a new password" screen in front of somebody who just logged in.
  const isRecovery = type === 'recovery'
  if (!isRecovery && !errorCode) return empty

  // Strip FIRST, before returning anything, so no caller can forget. Guarded:
  // a non-browser context (SSR, a test with no history) must not throw on the
  // login path.
  try {
    hist?.replaceState?.(null, '', `${loc.pathname}${loc.search}`)
  } catch {
    /* history unavailable — the value below is still never persisted */
  }

  if (errorCode) {
    return {
      grant: null,
      challenge: null,
      error: {
        code: errorCode,
        // GoTrue's own text is written for an end user ("Email link is invalid
        // or has expired"), so it is shown rather than replaced with a guess.
        description: params.get('error_description')?.replace(/\+/g, ' ')
          ?? 'That link is no longer valid.',
      },
    }
  }

  if (tokenHash) {
    if (!/^[A-Za-z0-9_-]{40,128}$/.test(tokenHash)) {
      return {
        grant: null,
        challenge: null,
        error: { code: 'invalid_token_hash', description: 'That reset link is not valid.' },
      }
    }
    return { grant: null, challenge: { tokenHash }, error: null }
  }

  if (!token) return empty

  return {
    grant: {
      accessToken: token,
      refreshToken: params.get('refresh_token'),
      email: claimEmail(token),
    },
    challenge: null,
    error: null,
  }
}
